import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { 
  Shield, 
  Server, 
  Network, 
  Users, 
  Cloud,
  Database,
  Monitor,
  Wrench,
  Phone,
  CheckCircle,
  ArrowRight,
  Zap,
  Lock,
  HardDrive,
  Wifi
} from "lucide-react";

const Services = () => {
  const serviceCategories = [
    {
      title: "Network Infrastructure",
      icon: <Network className="h-12 w-12" />,
      description: "Complete network design, implementation, and management solutions",
      image: "https://images.unsplash.com/photo-1506399309177-3b43e99fead2?w=600&auto=format&fit=crop&q=80",
      services: [
        {
          name: "Network Design & Architecture",
          description: "Custom network topology design tailored to your business needs",
          features: ["Site surveys", "Capacity planning", "Security integration", "Scalability assessment"]
        },
        {
          name: "Network Implementation",
          description: "Professional installation and configuration of network infrastructure",
          features: ["Hardware installation", "Configuration management", "Testing & validation", "Documentation"]
        },
        {
          name: "Network Monitoring & Management",
          description: "24/7 monitoring and proactive management of your network",
          features: ["Real-time monitoring", "Performance optimization", "Issue resolution", "Reporting"]
        }
      ]
    },
    {
      title: "Cybersecurity Solutions",
      icon: <Shield className="h-12 w-12" />,
      description: "Comprehensive security services to protect your business",
      image: "https://images.unsplash.com/photo-1660644807804-ffacfd7a4137?w=600&auto=format&fit=crop&q=80",
      services: [
        {
          name: "Security Assessment & Auditing",
          description: "Comprehensive evaluation of your current security posture",
          features: ["Vulnerability assessments", "Penetration testing", "Compliance audits", "Risk analysis"]
        },
        {
          name: "Firewall & Intrusion Prevention",
          description: "Advanced firewall solutions and intrusion detection systems",
          features: ["Next-gen firewalls", "IPS/IDS deployment", "Traffic analysis", "Threat intelligence"]
        },
        {
          name: "Incident Response & Recovery",
          description: "Rapid response to security incidents and recovery planning",
          features: ["24/7 incident response", "Forensic analysis", "Recovery procedures", "Prevention strategies"]
        }
      ]
    },
    {
      title: "IT Infrastructure",
      icon: <Server className="h-12 w-12" />,
      description: "End-to-end technology infrastructure consulting and implementation",
      image: "https://images.unsplash.com/photo-1506399309177-3b43e99fead2?w=600&auto=format&fit=crop&q=80",
      services: [
        {
          name: "Server Solutions",
          description: "Enterprise-grade server deployment and management",
          features: ["Server sizing", "Virtualization", "High availability", "Disaster recovery"]
        },
        {
          name: "Cloud Migration & Integration",
          description: "Seamless migration to cloud platforms and hybrid solutions",
          features: ["Cloud strategy", "Migration planning", "Hybrid integration", "Cost optimization"]
        },
        {
          name: "Data Center Services",
          description: "Complete data center design and management solutions",
          features: ["Rack & stack", "Power & cooling", "Cable management", "Environmental monitoring"]
        }
      ]
    },
    {
      title: "Managed IT Services",
      icon: <Users className="h-12 w-12" />,
      description: "Proactive IT support and management services",
      image: "https://images.unsplash.com/photo-1529961172671-d48e8280f846?w=600&auto=format&fit=crop&q=80",
      services: [
        {
          name: "Help Desk & Technical Support",
          description: "Comprehensive technical support for your end users",
          features: ["Multi-channel support", "Ticket management", "Remote assistance", "User training"]
        },
        {
          name: "Proactive Monitoring",
          description: "Continuous monitoring of your IT infrastructure",
          features: ["System monitoring", "Performance alerts", "Preventive maintenance", "Capacity planning"]
        },
        {
          name: "IT Consulting & Strategy",
          description: "Strategic IT planning and technology roadmap development",
          features: ["Technology assessment", "Strategic planning", "Budget planning", "Vendor management"]
        }
      ]
    }
  ];

  const additionalServices = [
    { icon: <Cloud className="h-6 w-6" />, name: "Cloud Services", description: "AWS, Azure, Google Cloud solutions" },
    { icon: <Database className="h-6 w-6" />, name: "Database Management", description: "Database design, optimization, and maintenance" },
    { icon: <Monitor className="h-6 w-6" />, name: "System Integration", description: "Seamless integration of disparate systems" },
    { icon: <Wrench className="h-6 w-6" />, name: "Hardware Procurement", description: "Technology hardware sales and procurement" },
    { icon: <Zap className="h-6 w-6" />, name: "Performance Optimization", description: "System and network performance tuning" },
    { icon: <Lock className="h-6 w-6" />, name: "Compliance Solutions", description: "Regulatory compliance and governance" },
    { icon: <HardDrive className="h-6 w-6" />, name: "Backup & Recovery", description: "Data backup and disaster recovery solutions" },
    { icon: <Wifi className="h-6 w-6" />, name: "Wireless Solutions", description: "Enterprise wireless network deployment" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20">
      <Navigation currentPage="services" />

      {/* Hero Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="mb-4" variant="secondary">
            Comprehensive IT Solutions
          </Badge>
          <h1 className="text-4xl lg:text-6xl font-bold text-foreground mb-6">
            Our <span className="bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">Services</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            From network infrastructure to cybersecurity, we provide comprehensive IT services 
            tailored to meet the unique needs of businesses and government agencies.
          </p>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {serviceCategories.map((category, index) => (
              <div key={index} className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="bg-primary/10 p-3 rounded-lg text-primary">
                      {category.icon}
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold text-foreground">{category.title}</h2>
                      <p className="text-muted-foreground">{category.description}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    {category.services.map((service, serviceIndex) => (
                      <Card key={serviceIndex} className="border-l-4 border-l-primary">
                        <CardHeader>
                          <CardTitle className="text-xl">{service.name}</CardTitle>
                          <CardDescription className="text-base">{service.description}</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-2 gap-2">
                            {service.features.map((feature, featureIndex) => (
                              <div key={featureIndex} className="flex items-center text-sm text-muted-foreground">
                                <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                                {feature}
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
                
                <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                  <div className="relative">
                    <img 
                      src={category.image}
                      alt={category.title}
                      className="rounded-2xl shadow-2xl w-full aspect-square object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 bg-gradient-to-r from-secondary/50 to-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Additional Specialized Services
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Beyond our core offerings, we provide specialized services to address 
              specific technology challenges and opportunities.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {additionalServices.map((service, index) => (
              <Card key={index} className="group hover:shadow-lg transition-all duration-300 text-center">
                <CardHeader>
                  <div className="bg-primary/10 p-3 rounded-lg w-fit mx-auto mb-4 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                    {service.icon}
                  </div>
                  <CardTitle className="text-lg">{service.name}</CardTitle>
                  <CardDescription>{service.description}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Served */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Industries We Serve
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Our diverse clientele spans multiple industries, from small local businesses 
              to large-scale enterprise organizations and government agencies.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="text-center">
              <CardHeader>
                <CardTitle className="text-xl">Small & Medium Businesses</CardTitle>
                <CardDescription>
                  Tailored IT solutions for growing businesses looking to leverage technology 
                  for competitive advantage and operational efficiency.
                </CardDescription>
              </CardHeader>
            </Card>
            
            <Card className="text-center">
              <CardHeader>
                <CardTitle className="text-xl">Enterprise Organizations</CardTitle>
                <CardDescription>
                  Comprehensive technology infrastructure and consulting services for 
                  large-scale organizations with complex IT requirements.
                </CardDescription>
              </CardHeader>
            </Card>
            
            <Card className="text-center">
              <CardHeader>
                <CardTitle className="text-xl">Government Agencies</CardTitle>
                <CardDescription>
                  Specialized IT services for government entities, ensuring compliance, 
                  security, and reliable technology infrastructure.
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary to-primary-glow text-primary-foreground">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Ready to Discuss Your IT Needs?
          </h2>
          <p className="text-xl mb-8 text-primary-foreground/90">
            Our experienced team is ready to help you design and implement the perfect 
            technology solution for your organization. Contact us for a free consultation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="secondary" className="bg-white text-primary hover:bg-white/90">
              <Link to="/contact" className="flex items-center">
                <Phone className="mr-2 h-5 w-5" />
                Schedule Consultation
              </Link>
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-primary">
              <Link to="/about" className="flex items-center">
                Learn More About Us
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Services;